import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider } from "@/components/ui/sidebar";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import LandingPage from "./pages/LandingPage";
import HomePage from "./pages/HomePage";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import AppelsOffres from "./pages/AppelsOffres";
import NouvelAppelOffres from "./pages/NouvelAppelOffres";
import DetailAppelOffres from "./pages/DetailAppelOffres";
import Societes from "./pages/Societes";
import ImportCV from "./pages/ImportCV";
import Analyses from "./pages/Analyses";
import RankingSocietes from "./pages/RankingSocietes";
import Parametres from "./pages/Parametres";
import PlanningAO from "./pages/PlanningAO";
import Services from "./pages/Services";
import GestionCollaborateurs from "./pages/GestionCollaborateurs";
import GestionUtilisateurs from "./pages/GestionUtilisateurs";
import Reporting from "./pages/Reporting";
import TypesAppelOffres from "./pages/TypesAppelOffres";
import TypeAppelOffresForm from "./pages/TypeAppelOffresForm";
import Architecture from "./pages/Architecture";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Chargement...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
};

// Home Route component - shows landing page for unauthenticated users
const HomeRoute = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Chargement...</div>
      </div>
    );
  }
  
  // Show landing page for unauthenticated users
  if (!user) {
    return <LandingPage />;
  }
  
  // Redirect authenticated users to home page
  return <Navigate to="/home" replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <TooltipProvider>
        <SidebarProvider>
          <div className="min-h-screen flex w-full">
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/" element={<HomeRoute />} />
              <Route path="/home" element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Index />
                </ProtectedRoute>
              } />
              <Route path="/appels-offres" element={
                <ProtectedRoute>
                  <AppelsOffres />
                </ProtectedRoute>
              } />
              <Route path="/appels-offres/nouveau" element={
                <ProtectedRoute>
                  <NouvelAppelOffres />
                </ProtectedRoute>
              } />
              <Route path="/appels-offres/:id" element={
                <ProtectedRoute>
                  <DetailAppelOffres />
                </ProtectedRoute>
              } />
              <Route path="/appels-offres/:id/edit" element={
                <ProtectedRoute>
                  <DetailAppelOffres />
                </ProtectedRoute>
              } />
              <Route path="/societes" element={
                <ProtectedRoute>
                  <Societes />
                </ProtectedRoute>
              } />
              <Route path="/import-cv" element={
                <ProtectedRoute>
                  <ImportCV />
                </ProtectedRoute>
              } />
              <Route path="/analyses" element={
                <ProtectedRoute>
                  <Analyses />
                </ProtectedRoute>
              } />
              <Route path="/ranking-societes" element={
                <ProtectedRoute>
                  <RankingSocietes />
                </ProtectedRoute>
              } />
              <Route path="/planning-ao" element={
                <ProtectedRoute>
                  <PlanningAO />
                </ProtectedRoute>
              } />
              <Route path="/services" element={
                <ProtectedRoute>
                  <Services />
                </ProtectedRoute>
              } />
              <Route path="/collaborateurs" element={
                <ProtectedRoute>
                  <GestionCollaborateurs />
                </ProtectedRoute>
              } />
              <Route path="/gestion-utilisateurs" element={
                <ProtectedRoute>
                  <GestionUtilisateurs />
                </ProtectedRoute>
              } />
              <Route path="/parametres" element={
                <ProtectedRoute>
                  <Parametres />
                </ProtectedRoute>
              } />
              <Route path="/reporting" element={
                <ProtectedRoute>
                  <Reporting />
                </ProtectedRoute>
              } />
              <Route path="/types-appel-offres" element={
                <ProtectedRoute>
                  <TypesAppelOffres />
                </ProtectedRoute>
              } />
              <Route path="/types-appel-offres/nouveau" element={
                <ProtectedRoute>
                  <TypeAppelOffresForm />
                </ProtectedRoute>
              } />
              <Route path="/types-appel-offres/:id" element={
                <ProtectedRoute>
                  <TypeAppelOffresForm />
                </ProtectedRoute>
              } />
              <Route path="/architecture" element={
                <ProtectedRoute>
                  <Architecture />
                </ProtectedRoute>
              } />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </SidebarProvider>
        <Toaster />
        <Sonner />
      </TooltipProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
